import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { ReportFilter } from '../../Interfaces/Reports/ReportFilter';
import { WidgetsState } from '../../Interfaces/Overview/WidgetsState';
import { TrendsFilter } from '../../Interfaces/Devices/Trends/TrendsFilter';
// Constants
import { ALERTS_REFRESH_RATE } from '../../utils/constants';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { IdentityManagerType } from '../../Interfaces/SSO/IdentityManagerConfiguration';

export enum UserSettingsActionTypes {
  SET_ALERTS_REFRESH_RATE = 'SET_ALERTS_REFRESH_RATE',
  SAVE_ALERTS_GRID_STATE = 'SAVE_ALERTS_GRID_STATE',
  SAVE_NODES_GRID_STATE = 'SAVE_NODES_GRID_STATE',
  SAVE_ASSET_STATUSES_GRID_STATE = 'SAVE_ASSET_STATUSES_GRID_STATE',
  SAVE_VERSIONS_GRID_STATE = 'SAVE_VERSIONS_GRID_STATE',
  SAVE_PRODUCTS_HEALTH_GRID_STATE = 'SAVE_PRODUCTS_HEALTH_GRID_STATE',
  SAVE_TICKETS_GRID_STATE = 'SAVE_TICKETS_GRID_STATE',
  SAVE_NODES_FILTERS = 'SAVE_NODES_FILTERS',
  SAVE_ASSET_STATUSES_FILTERS = 'SAVE_ASSET_STATUSES_FILTERS',
  SET_SHOW_ASSETS_SUMMARY = 'SET_SHOW_ASSETS_SUMMARY',
  SET_SHOW_ALERTS_SUMMARY = 'SET_SHOW_ALERTS_SUMMARY',
  SET_SHOW_DEVICE_STATUS_SUMMARY = 'SET_SHOW_DEVICE_STATUS_SUMMARY',
  SAVE_REPORT_FILTER = 'SAVE_REPORT_FILTER',
  SAVE_SELECTED_LANGUAGE = 'SAVE_SELECTED_LANGUAGE',
  SAVE_TICKET_FILTER = 'SAVE_TICKET_FILTER',
  SET_SHOW_TICKETS_SUMMARY = 'SET_SHOW_TICKETS_SUMMARY',
  SET_SHOW_HEALTH_SUMMARY = 'SET_SHOW_HEALTH_SUMMARY',
  SAVE_HEALTH_FILTER = 'SAVE_HEALTH_FILTER',
  SAVE_PRODUCTS_HEALTH_FILTER = 'SAVE_PRODUCTS_HEALTH_FILTER',
  SAVE_WIDGETS_STATE = 'SAVE_WIDGETS_STATE',
  SAVE_DEVICE_TRENDS_FILTER = 'SAVE_DEVICE_TRENDS_FILTER',
  SAVE_DEVICE_ISSUE_TRENDS_GRID_STATE = 'SAVE_DEVICE_ISSUE_TRENDS_GRID_STATE',
  SET_IDENTITY_MGR_TYPE = 'SET_IDENTITY_MGR_TYPE',
  SAVE_NODES_CONFIG_GRID_STATE = 'SAVE_NODES_CONFIG_GRID_STATE',
  SAVE_NODES_JSON_CONFIG_GRID_STATE = 'SAVE_NODES_JSON_CONFIG_GRID_STATE',
  SAVE_NODES_CONFIG_FILTERS = 'SAVE_NODES_CONFIG_FILTERS',
  SAVE_NODES_JSON_CONFIG_FILTERS = 'SAVE_NODES_JSON_CONFIG_FILTERS',
  SAVE_PREV_MAINTENANCE_GRID_STATE = 'SAVE_PREV_MAINTENANCE_GRID_STATE',
  SAVE_PREV_MAINTENANCE_FILTERS = 'SAVE_PREV_MAINTENANCE_FILTERS',
  SAVE_PRED_MAINTENANCE_GRID_STATE = 'SAVE_PRED_MAINTENANCE_GRID_STATE',
  SAVE_PRED_MAINTENANCE_FILTERS = 'SAVE_PRED_MAINTENANCE_FILTERS',
  SAVE_FIRMWARE_GRID_STATE = 'SAVE_FIRMWARE_GRID_STATE',
  SAVE_SELECTED_PROFILE = 'SAVE_SELECTED_PROFILE',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface UserSettingsState {
  identityMgrType: IdentityManagerType;
  refreshRate: number;
  alertsGridDataState: State;
  nodesGridDataState: State;
  assetStatusGridDataState: State;
  versionsGridDataState: State;
  productsHealthGridDataState: State;
  ticketsDataState: State;
  nodesFilters: CompositeFilterDescriptor;
  assetStatusesFilters: CompositeFilterDescriptor;
  ticketFilter: CompositeFilterDescriptor;
  reportFilter: ReportFilter;
  selectedLanguage: string;
  healthFilter: CompositeFilterDescriptor;
  productsHealthFilter: CompositeFilterDescriptor;
  widgetsState: WidgetsState[];
  deviceTrendsFilter: TrendsFilter;
  deviceTrendsByDateGridDataState: State;
  nodesConfigGridDataState: State;
  nodesJsonConfigGridDataState: State;
  nodesConfigFilters: CompositeFilterDescriptor;
  nodesJsonConfigFilters: CompositeFilterDescriptor;
  prevMaintenanceGridDataState: State;
  prevMaintenanceFilter: CompositeFilterDescriptor;
  predMaintenanceGridDataState: State;
  predMaintenanceFilter: CompositeFilterDescriptor;
  firmwareGridDataState: State;
  selectedProfile: string | undefined;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface OnSetIdentityMgrTypeAction {
  type: UserSettingsActionTypes.SET_IDENTITY_MGR_TYPE;
  identityMgrType: IdentityManagerType;
}

export interface SetAlertsRefreshRateAction {
  type: UserSettingsActionTypes.SET_ALERTS_REFRESH_RATE;
  value: number;
}

interface SaveAlertsGridStateAction {
  type: UserSettingsActionTypes.SAVE_ALERTS_GRID_STATE;
  gridState: State;
}

interface SaveNodesGridStateAction {
  type: UserSettingsActionTypes.SAVE_NODES_GRID_STATE;
  gridState: State;
}

interface SaveAssetStatusesGridStateAction {
  type: UserSettingsActionTypes.SAVE_ASSET_STATUSES_GRID_STATE;
  gridState: State;
}

interface SaveVersionsGridStateAction {
  type: UserSettingsActionTypes.SAVE_VERSIONS_GRID_STATE;
  gridState: State;
}

interface SaveProductsHealthGridStateAction {
  type: UserSettingsActionTypes.SAVE_PRODUCTS_HEALTH_GRID_STATE;
  gridState: State;
}

interface SaveTicketsGridStateAction {
  type: UserSettingsActionTypes.SAVE_TICKETS_GRID_STATE;
  gridState: State;
}

interface SaveNodesFiltersAction {
  type: UserSettingsActionTypes.SAVE_NODES_FILTERS;
  filters: CompositeFilterDescriptor;
}

interface SaveAssetStatusesFiltersAction {
  type: UserSettingsActionTypes.SAVE_ASSET_STATUSES_FILTERS;
  filters: CompositeFilterDescriptor;
}

interface SetShowAssetsSummary {
  type: UserSettingsActionTypes.SET_SHOW_ASSETS_SUMMARY;
  showAssetsSummary: boolean | undefined;
}

interface SetShowAlertsSummary {
  type: UserSettingsActionTypes.SET_SHOW_ALERTS_SUMMARY;
  showAlertsSummary: boolean | undefined;
}

interface SetShowDeviceStatusSummary {
  type: UserSettingsActionTypes.SET_SHOW_DEVICE_STATUS_SUMMARY;
  showDeviceStatusSummary: boolean | undefined;
}

interface SaveReportFilter {
  type: UserSettingsActionTypes.SAVE_REPORT_FILTER;
  reportFilter: ReportFilter;
}

interface SaveSelectedLanguage {
  type: UserSettingsActionTypes.SAVE_SELECTED_LANGUAGE;
  value: string;
}

interface SaveTicketFiltersAction {
  type: UserSettingsActionTypes.SAVE_TICKET_FILTER;
  filters: CompositeFilterDescriptor;
}

interface SetShowTicketsSummary {
  type: UserSettingsActionTypes.SET_SHOW_TICKETS_SUMMARY;
  showTicketsSummary: boolean | undefined;
}

interface SetShowHealthSummary {
  type: UserSettingsActionTypes.SET_SHOW_HEALTH_SUMMARY;
  showHealthSummary: boolean | undefined;
}

interface SaveHealthFiltersAction {
  type: UserSettingsActionTypes.SAVE_HEALTH_FILTER;
  filters: CompositeFilterDescriptor;
}
interface SaveProductsHealthFiltersAction {
  type: UserSettingsActionTypes.SAVE_PRODUCTS_HEALTH_FILTER;
  filters: CompositeFilterDescriptor;
}
interface SaveWidgetsStateAction {
  type: UserSettingsActionTypes.SAVE_WIDGETS_STATE;
  widgetsState: WidgetsState[];
}

interface SaveDeviceTrendsFiltersAction {
  type: UserSettingsActionTypes.SAVE_DEVICE_TRENDS_FILTER;
  deviceTrendsFilter: TrendsFilter;
}

interface SaveDeviceIssueTrendsGridStateAction {
  type: UserSettingsActionTypes.SAVE_DEVICE_ISSUE_TRENDS_GRID_STATE;
  gridState: State;
}

interface SaveNodesConfigGridStateAction {
  type: UserSettingsActionTypes.SAVE_NODES_CONFIG_GRID_STATE;
  gridState: State;
}

interface SaveNodesJsonConfigGridStateAction {
  type: UserSettingsActionTypes.SAVE_NODES_JSON_CONFIG_GRID_STATE;
  gridState: State;
}

interface SaveNodesConfigFiltersAction {
  type: UserSettingsActionTypes.SAVE_NODES_CONFIG_FILTERS;
  filters: CompositeFilterDescriptor;
}

interface SaveNodesJsonConfigFiltersAction {
  type: UserSettingsActionTypes.SAVE_NODES_JSON_CONFIG_FILTERS;
  filters: CompositeFilterDescriptor;
}

interface SavePrevMaintenanceGridStateAction {
  type: UserSettingsActionTypes.SAVE_PREV_MAINTENANCE_GRID_STATE;
  gridState: State;
}

interface SavePrevMaintenanceFilterAction {
  type: UserSettingsActionTypes.SAVE_PREV_MAINTENANCE_FILTERS;
  filters: CompositeFilterDescriptor;
}

interface SavePredMaintenanceGridStateAction {
  type: UserSettingsActionTypes.SAVE_PRED_MAINTENANCE_GRID_STATE;
  gridState: State;
}

interface SavePredMaintenanceFilterAction {
  type: UserSettingsActionTypes.SAVE_PRED_MAINTENANCE_FILTERS;
  filters: CompositeFilterDescriptor;
}

interface SaveFirmwareGridStateAction {
  type: UserSettingsActionTypes.SAVE_FIRMWARE_GRID_STATE;
  gridState: State;
}

interface SaveSelectedProfile {
  type: UserSettingsActionTypes.SAVE_SELECTED_PROFILE;
  selectedProfile: string | undefined;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | OnSetIdentityMgrTypeAction
  | SetAlertsRefreshRateAction
  | SaveAlertsGridStateAction
  | SaveNodesGridStateAction
  | SaveAssetStatusesGridStateAction
  | SaveNodesFiltersAction
  | SaveVersionsGridStateAction
  | SaveProductsHealthGridStateAction
  | SaveTicketsGridStateAction
  | SaveAssetStatusesFiltersAction
  | SetShowAssetsSummary
  | SetShowAlertsSummary
  | SetShowDeviceStatusSummary
  | SaveReportFilter
  | SaveSelectedLanguage
  | SaveTicketFiltersAction
  | SetShowTicketsSummary
  | SetShowHealthSummary
  | SaveHealthFiltersAction
  | SaveProductsHealthFiltersAction
  | SaveWidgetsStateAction
  | SaveDeviceTrendsFiltersAction
  | SaveDeviceIssueTrendsGridStateAction
  | SaveNodesConfigGridStateAction
  | SaveNodesJsonConfigGridStateAction
  | SaveNodesConfigFiltersAction
  | SaveNodesJsonConfigFiltersAction
  | SavePrevMaintenanceGridStateAction
  | SavePrevMaintenanceFilterAction
  | SavePredMaintenanceGridStateAction
  | SavePredMaintenanceFilterAction
  | SaveFirmwareGridStateAction
  | SaveSelectedProfile;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
export const actionCreators = {
  setIdentityMgrType:
    (idMgrType: IdentityManagerType): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      try {
        dispatch({
          type: UserSettingsActionTypes.SET_IDENTITY_MGR_TYPE,
          identityMgrType: idMgrType,
        });
      } catch (e) {
        console.error('SetIdentityMgrType failed');
      }
    },

  setAlertsRefreshRate:
    (value: number): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: UserSettingsActionTypes.SET_ALERTS_REFRESH_RATE,
        value: value,
      });
    },

  setSaveSelectedLanguage:
    (value: string): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: UserSettingsActionTypes.SAVE_SELECTED_LANGUAGE,
        value: value,
      });
    },

  saveUserSelectedAlertsFilter:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Alerts grid state`);
      if (!state.take) state.take = 100;

      dispatch({
        type: UserSettingsActionTypes.SAVE_ALERTS_GRID_STATE,
        gridState: state,
      });
    },

  saveVersionsGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Versions grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_VERSIONS_GRID_STATE,
        gridState: state,
      });
    },

  saveProductsHealthGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Products Health grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_PRODUCTS_HEALTH_GRID_STATE,
        gridState: state,
      });
    },

  saveTicketsGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Tickets grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_TICKETS_GRID_STATE,
        gridState: state,
      });
    },

  saveUserSelectedNodesFilter:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Nodes grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_NODES_GRID_STATE,
        gridState: state,
      });
    },

  saveAssetStatusesGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Asset (Device) statuses grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_ASSET_STATUSES_GRID_STATE,
        gridState: state,
      });
    },

  saveNodesFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Nodes filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_NODES_FILTERS,
        filters: filters,
      });
    },

  saveTicketFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Ticket filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_TICKET_FILTER,
        filters: filters,
      });
    },

  saveAssetStatusesFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Asset (Device) Status filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_ASSET_STATUSES_FILTERS,
        filters: filters,
      });
    },

  setAlertsSummaryVisibility:
    (value: boolean | undefined): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: UserSettingsActionTypes.SET_SHOW_ALERTS_SUMMARY,
        showAlertsSummary: value,
      });
    },

  setAssetsSummaryVisibility:
    (value: boolean | undefined): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: UserSettingsActionTypes.SET_SHOW_ASSETS_SUMMARY,
        showAssetsSummary: value,
      });
    },

  setDeviceStatusSummaryVisibility:
    (value: boolean | undefined): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: UserSettingsActionTypes.SET_SHOW_DEVICE_STATUS_SUMMARY,
        showDeviceStatusSummary: value,
      });
    },

  saveReportFilter:
    (reportFilter: ReportFilter): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${UserSettingsActionTypes.SAVE_REPORT_FILTER}`);

      dispatch({
        type: UserSettingsActionTypes.SAVE_REPORT_FILTER,
        reportFilter: reportFilter,
      });
    },

  setTicketsSummaryVisibility:
    (value: boolean | undefined): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: UserSettingsActionTypes.SET_SHOW_TICKETS_SUMMARY,
        showTicketsSummary: value,
      });
    },

  setHealthSummaryVisibility:
    (value: boolean | undefined): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: UserSettingsActionTypes.SET_SHOW_HEALTH_SUMMARY,
        showHealthSummary: value,
      });
    },

  saveHealthFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Health filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_HEALTH_FILTER,
        filters: filters,
      });
    },

  saveProductsHealthFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Products Health filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_PRODUCTS_HEALTH_FILTER,
        filters: filters,
      });
    },

  saveWidgetsState:
    (widgetsState: WidgetsState[]): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving widgets state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_WIDGETS_STATE,
        widgetsState: widgetsState,
      });
    },

  saveDeviceTrendsFilter:
    (filter: TrendsFilter): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected device trends filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_DEVICE_TRENDS_FILTER,
        deviceTrendsFilter: filter,
      });
    },

  saveDeviceIssueTrendsGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Device Issue Trends grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_DEVICE_ISSUE_TRENDS_GRID_STATE,
        gridState: state,
      });
    },

  saveUserSelectedNodesConfigGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Nodes config grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_NODES_CONFIG_GRID_STATE,
        gridState: state,
      });
    },

  saveUserSelectedNodesJsonConfigGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Nodes Json config grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_NODES_JSON_CONFIG_GRID_STATE,
        gridState: state,
      });
    },

  saveNodesConfigFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Nodes config filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_NODES_CONFIG_FILTERS,
        filters: filters,
      });
    },

  saveNodesJsonConfigFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Nodes Json config filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_NODES_JSON_CONFIG_FILTERS,
        filters: filters,
      });
    },

  savePrevMaintenanceGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Preventive Maintenance grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_PREV_MAINTENANCE_GRID_STATE,
        gridState: state,
      });
    },

  savePrevMaintenanceFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Preventive Maintenance filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_PREV_MAINTENANCE_FILTERS,
        filters: filters,
      });
    },

  savePredMaintenanceGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Predictive Maintenance grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_PRED_MAINTENANCE_GRID_STATE,
        gridState: state,
      });
    },

  savePredMaintenanceFilters:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Predictive Maintenance filters`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_PRED_MAINTENANCE_FILTERS,
        filters: filters,
      });
    },

  saveFirmwareGridState:
    (state: State): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Firmware grid state`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_FIRMWARE_GRID_STATE,
        gridState: state,
      });
    },

  saveSelectedProfile:
    (selectedProfile: string | undefined): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Profile`);
      dispatch({
        type: UserSettingsActionTypes.SAVE_SELECTED_PROFILE,
        selectedProfile: selectedProfile,
      });
    },
};

var defaultRefreshRate: number = ALERTS_REFRESH_RATE.DISABLED;

export var defaultLanguage: string = 'English';

export const initialDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'CreatedOn', dir: 'desc' }],
  group: [
    /*{ field: 'ItemDesc' }*/
  ],
};

export const initialAssetsDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'SiteNumber', dir: 'asc' }],
  group: [{ field: 'SiteNumber' }],
};

export const initialAssetsFilters: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialTicketFilters: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialAssetStatusesFilters: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialHealthFilters: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialProductsHealthFilters: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialAssetStatusesGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'SiteNumber', dir: 'asc' }],
  group: [],
};

export const initialVersionsGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'SiteNumber', dir: 'asc' }],
  group: [{ field: 'SiteNumber' }],
};

export const initialProductsHealthGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'SiteNumber', dir: 'asc' }],
  group: [{ field: 'SiteNumber' }],
};

export const initialTicketsGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'SiteNumber', dir: 'asc' }],
  group: [{ field: 'SiteNumber' }],
};

export const initialReportsFilter: ReportFilter = {
  Division: '',
  SiteNumbers: [],
  LaneNumbers: [],
  DateType: '',
  StartDateTime: new Date(),
  EndDateTime: new Date(),
  DateRange: ApiEnums.DateRangeOptions.Today,
};

export const initialNodesConfigGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'NodeInfo.SiteNumber', dir: 'asc' }],
  group: [{ field: 'NodeInfo.SiteNumber' }],
};

export const initialNodesJsonConfigGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'NodeInfo.SiteNumber', dir: 'asc' }],
  group: [{ field: 'NodeInfo.SiteNumber' }],
};

export const initialNodesConfigFilters: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [{ field: 'ConfigSetting', operator: 'eq' }],
};

export const initialNodesJsonConfigFilters: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [{ field: 'ConfigSetting', operator: 'eq' }],
};

export const initialPrevMaintenanceGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'Category', dir: 'asc' }],
  group: [],
};

export const initialPrevMaintenanceFilter: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialPredMaintenanceGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'Category', dir: 'asc' }],
  group: [],
};

export const initialFirmwareGridDataState: State = {
  skip: 0,
  take: 100,
  sort: [],
  group: [{ field: 'DeviceCategory' }],
};

export const initialPredMaintenanceFilter: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialWidgetsState: WidgetsState[] = [
  {
    id: ApiEnums.Widgets.AssetsSummary,
    nameKey: 'ASSETS_SUMMARY',
    name: 'Assets Summary',
    visible: true,
  },
  {
    id: ApiEnums.Widgets.DeviceStatusSummary,
    nameKey: 'DEVICE_STATUS_SUMMARY',
    name: 'Device Status Summary',
    visible: true,
  },
  {
    id: ApiEnums.Widgets.HealthSummary,
    nameKey: 'HEALTH_SUMMARY',
    name: 'Health Summary',
    visible: true,
  },
  {
    id: ApiEnums.Widgets.AlertsSummary,
    nameKey: 'ALERTS_SUMMARY',
    name: 'Alerts Summary',
    visible: true,
  },
  {
    id: ApiEnums.Widgets.TicketsSummary,
    nameKey: 'TICKETS_SUMMARY',
    name: 'Tickets Summary',
    visible: true,
  },
];

export const initialDeviceTrendsFilter: TrendsFilter = {
  AssetInfo: { Category: 'All', Name: 'CurrentIssue', Value: '' },
  Filters: { logic: 'and', filters: [] },
  StartDate: new Date(),
  EndDate: new Date(),
  DateRange: ApiEnums.DateRangeOptions.LastSevenDays,
};

export const initialDeviceTrendsByDateDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'Category', dir: 'asc' }],
  group: [],
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
// Everytime there's a new state added, Also define the new state(s) added  in the migration mapping(configureStore.ts) in an increment version

export const unloadedState: UserSettingsState = {
  refreshRate: defaultRefreshRate,
  alertsGridDataState: initialDataState,
  nodesGridDataState: initialAssetsDataState,
  assetStatusGridDataState: initialAssetStatusesGridDataState,
  versionsGridDataState: initialVersionsGridDataState,
  productsHealthGridDataState: initialProductsHealthGridDataState,
  ticketsDataState: initialTicketsGridDataState,
  assetStatusesFilters: initialAssetStatusesFilters,
  nodesFilters: initialAssetsFilters,
  ticketFilter: initialTicketFilters,
  reportFilter: initialReportsFilter,
  selectedLanguage: defaultLanguage,
  healthFilter: initialHealthFilters,
  productsHealthFilter: initialProductsHealthFilters,
  widgetsState: initialWidgetsState,
  deviceTrendsFilter: initialDeviceTrendsFilter,
  deviceTrendsByDateGridDataState: initialDeviceTrendsByDateDataState,
  identityMgrType: IdentityManagerType.Fake,
  nodesConfigGridDataState: initialNodesConfigGridDataState,
  nodesJsonConfigGridDataState: initialNodesJsonConfigGridDataState,
  nodesConfigFilters: initialNodesConfigFilters,
  nodesJsonConfigFilters: initialNodesJsonConfigFilters,
  prevMaintenanceGridDataState: initialPrevMaintenanceGridDataState,
  prevMaintenanceFilter: initialPrevMaintenanceFilter,
  predMaintenanceGridDataState: initialPredMaintenanceGridDataState,
  predMaintenanceFilter: initialPredMaintenanceFilter,
  firmwareGridDataState: initialFirmwareGridDataState,
  selectedProfile: undefined,
};

const UpdateWidgetsState = (
  state: WidgetsState[],
  id: number,
  result: boolean | undefined,
) => {
  var newWidgetState = [...state];

  let widgetIndex = state.findIndex((a: WidgetsState) => a.id === id);

  newWidgetState[widgetIndex].visible = result;

  return newWidgetState;
};

export const reducer: Reducer<UserSettingsState> = (
  state: UserSettingsState | undefined,
  incomingAction: Action,
): UserSettingsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case UserSettingsActionTypes.SET_IDENTITY_MGR_TYPE:
      return {
        ...state,
        identityMgrType: action.identityMgrType,
      };

    case UserSettingsActionTypes.SET_ALERTS_REFRESH_RATE:
      return {
        ...state,
        refreshRate: action.value,
      };
    case UserSettingsActionTypes.SAVE_ALERTS_GRID_STATE:
      return {
        ...state,
        alertsGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_NODES_GRID_STATE:
      return {
        ...state,
        nodesGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_ASSET_STATUSES_GRID_STATE:
      return {
        ...state,
        assetStatusGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_VERSIONS_GRID_STATE:
      return {
        ...state,
        versionsGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_PRODUCTS_HEALTH_GRID_STATE:
      return {
        ...state,
        productsHealthGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_TICKETS_GRID_STATE:
      return {
        ...state,
        ticketsDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_NODES_FILTERS:
      return {
        ...state,
        nodesFilters: action.filters,
      };
    case UserSettingsActionTypes.SAVE_ASSET_STATUSES_FILTERS:
      return {
        ...state,
        assetStatusesFilters: action.filters,
      };
    case UserSettingsActionTypes.SET_SHOW_ALERTS_SUMMARY:
      return {
        ...state,
        widgetsState: UpdateWidgetsState(
          state.widgetsState,
          4,
          action.showAlertsSummary,
        ),
      };
    case UserSettingsActionTypes.SET_SHOW_ASSETS_SUMMARY:
      return {
        ...state,
        widgetsState: UpdateWidgetsState(
          state.widgetsState,
          1,
          action.showAssetsSummary,
        ),
      };
    case UserSettingsActionTypes.SET_SHOW_DEVICE_STATUS_SUMMARY:
      return {
        ...state,
        widgetsState: UpdateWidgetsState(
          state.widgetsState,
          2,
          action.showDeviceStatusSummary,
        ),
      };
    case UserSettingsActionTypes.SAVE_REPORT_FILTER:
      return {
        ...state,
        reportFilter: action.reportFilter,
      };
    case UserSettingsActionTypes.SAVE_SELECTED_LANGUAGE:
      return {
        ...state,
        selectedLanguage: action.value,
      };
    case UserSettingsActionTypes.SAVE_TICKET_FILTER:
      return {
        ...state,
        ticketFilter: action.filters,
      };
    case UserSettingsActionTypes.SET_SHOW_TICKETS_SUMMARY:
      return {
        ...state,
        widgetsState: UpdateWidgetsState(
          state.widgetsState,
          5,
          action.showTicketsSummary,
        ),
      };
    case UserSettingsActionTypes.SET_SHOW_HEALTH_SUMMARY:
      return {
        ...state,
        widgetsState: UpdateWidgetsState(
          state.widgetsState,
          3,
          action.showHealthSummary,
        ),
      };
    case UserSettingsActionTypes.SAVE_HEALTH_FILTER:
      return {
        ...state,
        healthFilter: action.filters,
      };
    case UserSettingsActionTypes.SAVE_PRODUCTS_HEALTH_FILTER:
      return {
        ...state,
        productsHealthFilter: action.filters,
      };
    case UserSettingsActionTypes.SAVE_WIDGETS_STATE:
      return {
        ...state,
        widgetsState: action.widgetsState,
      };
    case UserSettingsActionTypes.SAVE_DEVICE_TRENDS_FILTER:
      return {
        ...state,
        deviceTrendsFilter: action.deviceTrendsFilter,
      };
    case UserSettingsActionTypes.SAVE_DEVICE_ISSUE_TRENDS_GRID_STATE:
      return {
        ...state,
        deviceTrendsByDateGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_NODES_CONFIG_GRID_STATE:
      return {
        ...state,
        nodesConfigGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_NODES_JSON_CONFIG_GRID_STATE:
      return {
        ...state,
        nodesJsonConfigGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_NODES_CONFIG_FILTERS:
      return {
        ...state,
        nodesConfigFilters: action.filters,
      };
    case UserSettingsActionTypes.SAVE_NODES_JSON_CONFIG_FILTERS:
      return {
        ...state,
        nodesJsonConfigFilters: action.filters,
      };
    case UserSettingsActionTypes.SAVE_PREV_MAINTENANCE_GRID_STATE:
      return {
        ...state,
        prevMaintenanceGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_PREV_MAINTENANCE_FILTERS:
      return {
        ...state,
        prevMaintenanceFilter: action.filters,
      };
    case UserSettingsActionTypes.SAVE_PRED_MAINTENANCE_GRID_STATE:
      return {
        ...state,
        predMaintenanceGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_PRED_MAINTENANCE_FILTERS:
      return {
        ...state,
        predMaintenanceFilter: action.filters,
      };
    case UserSettingsActionTypes.SAVE_FIRMWARE_GRID_STATE:
      return {
        ...state,
        firmwareGridDataState: action.gridState,
      };
    case UserSettingsActionTypes.SAVE_SELECTED_PROFILE:
      return {
        ...state,
        selectedProfile: action.selectedProfile,
      };
    default:
      break;
  }

  return state;
};
